/*
 * Settings → Operations → NDT Inspection (GET / PUT /ndt-inspection/settings,
 * fieldiq-api#1134).
 *
 * A tenant with no row reads the API's defaults with `configured: false`, and
 * the page says so: those values are in effect but nobody chose them. The
 * default ticket mode is one per crew-day (Jason, 2026-10-19).
 *
 * Only the fields that changed are sent; the API's PUT keeps what it is not
 * handed. The dose investigation level is shown and sent in the tenant's dose
 * unit (`meta.units.dose`); the API stores mSv and converts.
 *
 * Writes are NDT_ADMIN (admin, office_staff — owner is NOT on the list);
 * everyone else reads the values with the controls disabled.
 */
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useNdtCatalog, useNdtSettings, useUpdateNdtSettings } from '../../hooks/useNdtInspection';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canAdminNdt, inputValue, ndtErrorMessage, NDT_TICKET_MODE_LABEL, numberFromInput } from '../../lib/ndt-inspection';
import type { NdtSettingsUpdate, NdtTicketMode } from '../../types/ndt-inspection';

const inputCls =
  'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm placeholder-fieldiq-muted focus:outline-none focus:border-fieldiq-accent/50 disabled:opacity-60 disabled:cursor-not-allowed';

/** "60, 30, 7" → [60, 30, 7]; null when any part is not a whole number. The API validates the range. */
function parseWarnDays(text: string): number[] | null {
  const parts = text.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return null;
  const nums = parts.map(Number);
  return nums.every(Number.isInteger) ? nums : null;
}
const sameDays = (a: string, b: string) => a.replace(/\s/g, '') === b.replace(/\s/g, '');

export default function NdtSettingsPage() {
  const { user } = useAuth();
  const canWrite = canAdminNdt(user?.role);
  const query = useNdtSettings();
  const catalog = useNdtCatalog();
  const save = useUpdateNdtSettings();
  const settings = query.data?.data ?? null;
  const doseUnit = query.data?.meta.units?.dose ?? null;
  const failure = queryLoadFailure(query);

  const [ticketMode, setTicketMode] = useState<NdtTicketMode>('per_day');
  const [code, setCode] = useState('');
  const [calWarn, setCalWarn] = useState('');
  const [expiryWarn, setExpiryWarn] = useState('');
  const [dose, setDose] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (settings) {
      setTicketMode(settings.defaultTicketMode);
      setCode(settings.defaultAcceptanceCode ?? '');
      setCalWarn(settings.calWarnDays.join(', '));
      setExpiryWarn(settings.expiryWarnDays.join(', '));
      setDose(inputValue(settings.doseInvestigationLevel));
    }
  }, [settings]);

  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" aria-label="Loading NDT settings" />
      </div>
    );
  }
  if (failure || !settings) {
    return (
      <div className="px-6 py-4">
        <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="NDT settings" />
      </div>
    );
  }

  const initialCal = settings.calWarnDays.join(', ');
  const initialExpiry = settings.expiryWarnDays.join(', ');
  const initialDose = inputValue(settings.doseInvestigationLevel);
  const dirty =
    ticketMode !== settings.defaultTicketMode ||
    code !== (settings.defaultAcceptanceCode ?? '') ||
    !sameDays(calWarn, initialCal) ||
    !sameDays(expiryWarn, initialExpiry) ||
    dose.trim() !== initialDose;

  const onSave = () => {
    setError(null);
    const body: NdtSettingsUpdate = {};
    if (ticketMode !== settings.defaultTicketMode) body.default_ticket_mode = ticketMode;
    if (code !== (settings.defaultAcceptanceCode ?? '')) body.default_acceptance_code = code || null;
    if (!sameDays(calWarn, initialCal)) {
      const days = parseWarnDays(calWarn);
      if (!days) return setError('Calibration warning days must be whole numbers separated by commas, e.g. 60, 30, 7.');
      body.cal_warn_days = days;
    }
    if (!sameDays(expiryWarn, initialExpiry)) {
      const days = parseWarnDays(expiryWarn);
      if (!days) return setError('Expiry warning days must be whole numbers separated by commas, e.g. 60, 30, 7.');
      body.expiry_warn_days = days;
    }
    if (dose.trim() !== initialDose) {
      const n = numberFromInput(dose);
      if (Number.isNaN(n)) return setError('The dose investigation level must be a number.');
      body.dose_investigation_level = n;
    }
    save.mutate(body, {
      onSuccess: () => toast.success('NDT settings saved'),
      onError: (err) => setError(ndtErrorMessage(err, 'The settings could not be saved.')),
    });
  };

  return (
    <div className="min-h-full" data-testid="ndt-settings-page">
      <div className="px-6 py-4 space-y-4">
        {canWrite ? (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onSave}
              disabled={!dirty || save.isPending}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                dirty ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'bg-fieldiq-border text-fieldiq-muted cursor-not-allowed'
              }`}
            >
              {save.isPending ? <Loader2 size={16} className="animate-spin" /> : null}
              {save.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        ) : (
          <p className="text-xs text-fieldiq-muted">Only an admin or office staff can change NDT settings.</p>
        )}

        {!settings.configured && (
          <p className="text-xs text-fieldiq-muted" data-testid="ndt-settings-defaults">
            These are the defaults. Nobody has saved NDT settings for this company yet.
          </p>
        )}
        {error && (
          <p role="alert" className="rounded-lg border border-fi-signal-red/40 bg-fi-signal-red/10 px-3 py-2 text-sm text-fieldiq-text">
            {error}
          </p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div>
            <label htmlFor="ndt-ticket-mode" className="text-sm font-medium text-white block mb-1">Default ticket mode</label>
            <select id="ndt-ticket-mode" className={inputCls} value={ticketMode} disabled={!canWrite} onChange={(e) => setTicketMode(e.target.value as NdtTicketMode)}>
              <option value="per_day">{NDT_TICKET_MODE_LABEL.per_day}</option>
              <option value="per_job">{NDT_TICKET_MODE_LABEL.per_job}</option>
            </select>
            <p className="text-xs text-fieldiq-muted mt-1">How field tickets are cut when a customer has no setting.</p>
          </div>
          <div>
            <label htmlFor="ndt-code" className="text-sm font-medium text-white block mb-1">Default acceptance code</label>
            <select id="ndt-code" className={inputCls} value={code} disabled={!canWrite} onChange={(e) => setCode(e.target.value)}>
              <option value="">None</option>
              {(catalog.data?.data?.codes ?? []).map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-fieldiq-muted mt-1">Pre-selected on new jobs.</p>
          </div>
          <div>
            <label htmlFor="ndt-dose-level" className="text-sm font-medium text-white block mb-1">
              Dose investigation level{doseUnit ? ` (${doseUnit})` : ''}
            </label>
            <input id="ndt-dose-level" className={inputCls} inputMode="decimal" value={dose} disabled={!canWrite} onChange={(e) => setDose(e.target.value)} placeholder="Not set" />
            <p className="text-xs text-fieldiq-muted mt-1">A deep dose at or over this is flagged. Blank turns the flag off.</p>
          </div>
          <div>
            <label htmlFor="ndt-cal-warn" className="text-sm font-medium text-white block mb-1">Calibration warning days</label>
            <input id="ndt-cal-warn" className={inputCls} inputMode="numeric" value={calWarn} disabled={!canWrite} onChange={(e) => setCalWarn(e.target.value)} />
            <p className="text-xs text-fieldiq-muted mt-1">Up to five, e.g. 60, 30, 7. The largest starts Due soon.</p>
          </div>
          <div>
            <label htmlFor="ndt-expiry-warn" className="text-sm font-medium text-white block mb-1">Expiry warning days</label>
            <input id="ndt-expiry-warn" className={inputCls} inputMode="numeric" value={expiryWarn} disabled={!canWrite} onChange={(e) => setExpiryWarn(e.target.value)} />
            <p className="text-xs text-fieldiq-muted mt-1">For certs, welder quals and leak tests.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
